import { SCHEDULE_TIMEZONE } from "@Simplicity-Education/core";
import { QueryClient, useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useParams } from "react-router";
import { apiFetch } from "../../../api";
import { formatAbsoluteTime, formatDurationMinutes } from "../../../lib/formatRelativeTime";

/** One row of `GET /api/admin/connectors/:id/schedule-status` (SIM-3402). */
export interface ScheduleStatus {
  schedule: string;
  /** ISO. An estimate, never a promise — see NEXT_DUE_NOTE. */
  nextDueAt: string;
  dueNow: boolean;
  running: {
    startedAt: string;
    verdict: "healthy" | "bootstrap-stall" | "stalled" | "exhausted";
  } | null;
}

export const NEXT_DUE_NOTE =
  "Estimate. The scheduler picks this up on its next tick, after any runs queued ahead of it.";

// The connector page has no QueryClientProvider; one client here lets every row share a
// single fetch without adding one. Polled, because "running" is the whole point of asking.
const statusClient = new QueryClient({ defaultOptions: { queries: { retry: false } } });
const REFRESH_MS = 2 * 60_000;

/** The schedule status for the connector in the route, or null off a connector page. */
export function useScheduleStatus(): UseQueryResult<ScheduleStatus[]> | null {
  const { id } = useParams<{ id: string }>();
  const query = useQuery(
    {
      queryKey: ["schedule-status", id],
      queryFn: () => apiFetch<ScheduleStatus[]>(`/api/admin/connectors/${id}/schedule-status`),
      enabled: id !== undefined,
      refetchInterval: REFRESH_MS,
    },
    statusClient,
  );
  return id === undefined ? null : query;
}

export type ScheduleStatusView =
  | { kind: "running"; text: string; title: string; stalledVerdict: string | null }
  | { kind: "due-now"; text: string; title: string }
  | { kind: "estimate"; text: string; title: string };

const timeOfDay = new Intl.DateTimeFormat("en-US", {
  timeZone: SCHEDULE_TIMEZONE,
  hour: "numeric",
  minute: "2-digit",
});

/** What a schedule row says. Times of day are ET, the zone its "Runs at" is read in. */
export function describeScheduleStatus(status: ScheduleStatus, nowMs: number): ScheduleStatusView {
  if (status.running) {
    const startedMs = Date.parse(status.running.startedAt);
    const { verdict } = status.running;
    return {
      kind: "running",
      text: `Running since ${timeOfDay.format(startedMs)}`,
      title: formatAbsoluteTime(startedMs),
      stalledVerdict: verdict === "healthy" ? null : verdict,
    };
  }
  if (status.dueNow) return { kind: "due-now", text: "Due now", title: NEXT_DUE_NOTE };

  const dueMs = Date.parse(status.nextDueAt);
  const minutes = Math.floor((dueMs - nowMs) / 60_000);
  return {
    kind: "estimate",
    text: minutes < 1 ? "Next due ≈ now" : `Next due ≈ in ${formatDurationMinutes(minutes)}`,
    title: `${NEXT_DUE_NOTE} ${formatAbsoluteTime(dueMs)}.`,
  };
}
